import React, { useState, useCallback, useRef, useMemo, useEffect } from "react";
import { usePartnerFormStore } from "../store/usePartnerFormStore";
import { NavigationButtons, Input } from "./ui";
import { MapContainer, TileLayer, Marker, Polygon, Tooltip, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { getAllHexagons } from "../apis/hexagons";
import { cellToBoundary, gridDisk } from "h3-js";
import type { HexagonData } from "../apis/hexagons";

// Fix for Leaflet marker icon in React
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png",
  iconUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png",
  shadowUrl: "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png",
});

const GOOGLE_KEY = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;
const TILE_URL = GOOGLE_KEY
  ? `https://mt1.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&key=${GOOGLE_KEY}`
  : "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png";
const TILE_ATTRIBUTION = GOOGLE_KEY
  ? "&copy; Google Maps"
  : '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>';

// Default center (Kochi, Kerala)
const DEFAULT_CENTER: [number, number] = [9.9312, 76.2673];
const DEFAULT_ZOOM = 7;

function MapResizer({ isFullscreen }: { isFullscreen: boolean }) {
  const map = useMap();
  useEffect(() => {
    const timeouts = [0, 50, 100, 200, 300].map(ms => 
      setTimeout(() => { map.invalidateSize(); }, ms)
    );
    return () => timeouts.forEach(clearTimeout);
  }, [isFullscreen, map]);
  return null;
}

// ─── Map sub-components ───────────────────────────────────────────────────────

interface LocationMarkerProps {
  onLocationSelect: (lat: number, lng: number) => void;
  selectedLocation: [number, number] | null;
}

function LocationMarker({ onLocationSelect, selectedLocation }: LocationMarkerProps) {
  const markerRef = useRef<L.Marker>(null);

  useMapEvents({
    click(e) {
      onLocationSelect(e.latlng.lat, e.latlng.lng);
    },
  });

  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker != null) {
          const latLng = marker.getLatLng();
          onLocationSelect(latLng.lat, latLng.lng);
        }
      },
    }),
    [onLocationSelect]
  );

  return selectedLocation ? (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={selectedLocation}
      ref={markerRef}
    />
  ) : null;
}

function FlyToLocation({ position }: { position: [number, number] | null }) {
  const map = useMapEvents({});
  React.useEffect(() => {
    if (position) {
      map.flyTo(position, 14, { animate: true, duration: 1.2 });
    }
  }, [map, position]);
  return null;
}

// ─── Hexagon overlay on the branch location map ───────────────────────────────

interface HexOverlayProps {
  hexagons: HexagonData[];
  selectedHexagons: string[];
  onToggle: (h3Index: string) => void;
  maxSelections: number | null;
}

function HexOverlay({ hexagons, selectedHexagons, onToggle, maxSelections }: HexOverlayProps) {
  const selectedSet = useMemo(() => new Set(selectedHexagons), [selectedHexagons]);
  const atMax = maxSelections !== null && selectedHexagons.length >= maxSelections;

  const adjacentSet = useMemo(() => {
    const adj = new Set<string>();
    if (selectedSet.size > 0) {
      for (const hex of selectedSet) {
        try {
          const neighbors = gridDisk(hex, 1);
          for (const n of neighbors) {
            if (!selectedSet.has(n)) {
              adj.add(n);
            }
          }
        } catch (e) {
          console.error("Error computing neighbors:", e);
        }
      }
    }
    return adj;
  }, [selectedSet]);

  return (
    <>
      {hexagons.map((hex) => {
        const isAvailable = hex.isAvailable !== false;
        const isSelected = selectedSet.has(hex.h3Index);
        const isAdjacent = adjacentSet.has(hex.h3Index);
        
        const isAdjacencyRestricted = selectedSet.size > 0 && !isAdjacent && !isSelected;
        const isDisabled = (atMax && !isSelected) || isAdjacencyRestricted;

        let fillColor, strokeColor, fillOpacity;

        if (!isAvailable) {
          fillColor = "#ef4444";
          strokeColor = "#dc2626";
          fillOpacity = 0.45;
        } else if (isSelected) {
          fillColor = "#2196F3";
          strokeColor = "#1565C0";
          fillOpacity = 0.6;
        } else if (selectedSet.size > 0 && isAdjacent && !atMax) {
          fillColor = "#4CAF50";
          strokeColor = "#2E7D32";
          fillOpacity = 0.4;
        } else {
          fillColor = "transparent";
          strokeColor = "#9e9e9e";
          fillOpacity = 0;
        }

        const clickable = isAvailable && (!isDisabled || isSelected);

        const positions: [number, number][] = hex.boundary.map(([lat, lon]) => [lat, lon]);

        return (
          <Polygon
            key={hex.h3Index}
            positions={positions}
            pathOptions={{
              color: strokeColor,
              fillColor,
              fillOpacity,
              weight: 2,
              dashArray: !isAvailable ? "4" : undefined
            }}
            eventHandlers={{
              click: () => {
                if (clickable) onToggle(hex.h3Index);
              },
            }}
          >
            <Tooltip sticky>
              {!isAvailable
                ? (hex.claimedBy?.partnerName ? `Claimed by: ${hex.claimedBy.partnerName}` : "Already Claimed / Unavailable")
                : isSelected
                ? "Selected"
                : atMax
                ? "Maximum limit reached"
                : isAdjacencyRestricted
                ? "Only adjacent hexagons can be selected"
                : "Available (click to select)"}
            </Tooltip>
          </Polygon>
        );
      })}
    </>
  );
}

// ─── Static data ──────────────────────────────────────────────────────────────

const branchTypes = [
  { value: "main", label: "Main Branch", icon: "🏢" },
  { value: "franchise", label: "Franchise", icon: "🏪" },
  { value: "outlet", label: "Outlet", icon: "🏬" },
  { value: "service_center", label: "Service Center", icon: "🔧" },
];

const daysOfWeek = [
  { value: "monday", label: "Monday", shortLabel: "Mon" },
  { value: "tuesday", label: "Tuesday", shortLabel: "Tue" },
  { value: "wednesday", label: "Wednesday", shortLabel: "Wed" },
  { value: "thursday", label: "Thursday", shortLabel: "Thu" },
  { value: "friday", label: "Friday", shortLabel: "Fri" },
  { value: "saturday", label: "Saturday", shortLabel: "Sat" },
  { value: "sunday", label: "Sunday", shortLabel: "Sun" },
];

// ─── Main component ───────────────────────────────────────────────────────────

const PartnerFormStep3Branch: React.FC = () => {
  const {
    branchDetails,
    updateBranchDetails,
    coverageAreas,
    previousStep,
    nextStep,
    errors,
    setError,
    clearError,
    setSelectedHexagons,
    serviceDetails,
    submittedFormId,
  } = usePartnerFormStore();

  const selectedPlan = coverageAreas.selectedPlan as any;
  const isCustom = selectedPlan?.type === 'custom' || selectedPlan?.name?.toLowerCase().includes('custom');
  const planHexLimit: number | null = isCustom
    ? null
    : selectedPlan?.features?.hexCount !== undefined && selectedPlan.features.hexCount !== null
    ? selectedPlan.features.hexCount
    : null;
  const isAllKerala = !isCustom && selectedPlan?.features?.hexCount === 0;
  const costPerHex = selectedPlan?.costPerHex || selectedPlan?.pricing?.costPerHex || 0;

  // Local location state
  const [selectedLocation, setSelectedLocation] = useState<[number, number] | null>(
    branchDetails?.location?.coordinates
      ? [branchDetails.location.coordinates[1], branchDetails.location.coordinates[0]]
      : null
  );

  // Nearby hexagons as HexagonData (has boundary info needed for map)
  const [nearbyHexData, setNearbyHexData] = useState<HexagonData[]>([]);
  const [isLoadingHexagons, setIsLoadingHexagons] = useState(false);
  const [locationFetched, setLocationFetched] = useState(false);
  const [isLocationConfirmed, setIsLocationConfirmed] = useState(
    !!branchDetails?.location?.coordinates
  );

  // Address search state
  const [addressSearch, setAddressSearch] = useState(
    branchDetails?.location?.address || ""
  );
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Currently selected hexagons (from store)
  const selectedHexagons = coverageAreas.selectedHexagons;

  // Operating hours state for better UX
  const [expandedHours, setExpandedHours] = useState(true);

  const [isMapFullscreen, setIsMapFullscreen] = useState(false);
  const mapWrapperRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsMapFullscreen(!!document.fullscreenElement);
    };
    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      mapWrapperRef.current?.requestFullscreen().catch(err => {
        console.error("Error attempting to enable fullscreen:", err);
      });
    } else {
      document.exitFullscreen().catch(err => {
        console.error("Error attempting to exit fullscreen:", err);
      });
    }
  };

  // ── Address search (Nominatim) ─────────────────────────────────────────────

  const searchAddress = useCallback(async (query: string) => {
    if (!query || query.trim().length < 3) {
      setSearchResults([]);
      return;
    }
    setIsSearching(true);
    try {
      const url = `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&q=${encodeURIComponent(query)}&countrycodes=in&limit=6`;
      const res = await fetch(url, { headers: { "Accept-Language": "en" } });
      const data = await res.json();
      setSearchResults(data);
    } catch {
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  }, []);

  const handleAddressInputChange = (value: string) => {
    setAddressSearch(value);
    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    searchTimeoutRef.current = setTimeout(() => searchAddress(value), 500);
  };

  // ── Fetch nearby hexagons and convert to HexagonData ──────────────────────

  const fetchNearbyHexagons = useCallback(async (_lat: number, _lng: number, clearPreviousSelection: boolean = true) => {
    setIsLoadingHexagons(true);
    setNearbyHexData([]);
    try {
      const subcategories = serviceDetails?.services?.subcategories || [];
      const category = serviceDetails?.services?.category;
      const allHexs = await getAllHexagons(subcategories, category, submittedFormId);

      const hexData: HexagonData[] = allHexs.map((h) => {
        const rawBoundary = h.boundary || cellToBoundary(h.h3Index);
        return {
          h3Index: h.h3Index,
          centerLat: h.centerLat,
          centerLon: h.centerLon,
          boundary: rawBoundary,
          isAvailable: h.isAvailable !== false,
          claimedForSubcategory: false,
          claimedBy: h.claimedBy,
        };
      });

      setNearbyHexData(hexData);
      setLocationFetched(true);
      if (clearPreviousSelection) {
        setSelectedHexagons([]);
      }
      updateBranchDetails({ nearbyHexagons: hexData.map((h) => h.h3Index) });
    } catch (err) {
      console.error("Error fetching nearby hexagons:", err);
    } finally {
      setIsLoadingHexagons(false);
    }
  }, [setSelectedHexagons, updateBranchDetails, serviceDetails, submittedFormId]);

  // Fetch nearby hexagons on mount if location is already selected and confirmed
  React.useEffect(() => {
    if (selectedLocation && nearbyHexData.length === 0 && !locationFetched && !isLoadingHexagons) {
      fetchNearbyHexagons(selectedLocation[0], selectedLocation[1], false);
    }
  }, [selectedLocation, nearbyHexData.length, locationFetched, isLoadingHexagons, fetchNearbyHexagons]);

  // ── Location select (map click or geocode) ─────────────────────────────────

  const handleLocationFieldChange = useCallback((field: string, value: string) => {
    if (branchDetails?.location) {
      updateBranchDetails({
        location: {
          ...branchDetails.location,
          [field]: value,
        },
      });
      if (field === "city" && value.trim().length > 0) {
        clearError("location");
      }
    }
  }, [branchDetails?.location, updateBranchDetails, clearError]);

  const handleLocationSelect = useCallback(async (
    lat: number,
    lng: number,
    addressOverride?: Partial<{ address: string; city: string; district: string; state: string; pincode: string }>
  ) => {
    setSelectedLocation([lat, lng]);
    
    let resolvedAddress = addressOverride;
    
    if (!addressOverride) {
      try {
        const url = `https://nominatim.openstreetmap.org/reverse?format=json&lat=${lat}&lon=${lng}&addressdetails=1`;
        const res = await fetch(url, { headers: { "Accept-Language": "en" } });
        const data = await res.json();
        if (data && data.display_name) {
          setAddressSearch(data.display_name);
          const rawAddr = data.address || {};
          const resolvedCity = rawAddr.city || rawAddr.town || rawAddr.village || rawAddr.suburb || rawAddr.neighbourhood || rawAddr.municipality || rawAddr.city_district || rawAddr.county || rawAddr.state_district || "Unknown";
          resolvedAddress = {
            address: data.display_name,
            city: resolvedCity,
            district: rawAddr.county || rawAddr.state_district || "",
            state: rawAddr.state || "",
            pincode: rawAddr.postcode || "",
          };
        }
      } catch (err) {
        console.error("Reverse geocoding error:", err);
      }
    }

    updateBranchDetails({
      location: {
        type: "Point",
        coordinates: [lng, lat],
        ...resolvedAddress,
      },
    });
    clearError("location");
    await fetchNearbyHexagons(lat, lng, true);
  }, [updateBranchDetails, clearError, fetchNearbyHexagons]);

  const handleSelectSearchResult = async (result: any) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);
    setAddressSearch(result.display_name);
    setSearchResults([]);
    const rawAddr = result.address || {};
    const resolvedCity = rawAddr.city || rawAddr.town || rawAddr.village || rawAddr.suburb || rawAddr.neighbourhood || rawAddr.municipality || rawAddr.city_district || rawAddr.county || rawAddr.state_district || "Unknown";
    const addressOverride = {
      address: result.display_name,
      city: resolvedCity,
      district: rawAddr.county || rawAddr.state_district || "",
      state: rawAddr.state || "",
      pincode: rawAddr.postcode || "",
    };
    await handleLocationSelect(lat, lng, addressOverride);
  };

  // ── Hexagon toggle ─────────────────────────────────────────────────────────

  const handleHexToggle = useCallback(async (h3Index: string) => {
    const limit = planHexLimit ?? Infinity;
    const isSelected = selectedHexagons.includes(h3Index);
    if (isSelected) {
      setSelectedHexagons(selectedHexagons.filter((h) => h !== h3Index));
    } else if (selectedHexagons.length < limit) {
      setSelectedHexagons([...selectedHexagons, h3Index]);
    }
  }, [selectedHexagons, planHexLimit, setSelectedHexagons]);

  // ── Form field helpers ─────────────────────────────────────────────────────

  const handleInputChange = (field: string, value: any) => {
    updateBranchDetails({ [field]: value });
    clearError(field);
  };

  const handleContactPersonChange = (field: string, value: string) => {
    updateBranchDetails({
      contactPerson: {
        ...branchDetails?.contactPerson,
        [field]: value,
      },
    });
  };

  const handleOperatingHoursChange = (day: string, field: string, value: any) => {
    const currentHours = branchDetails?.operatingHours || [];
    const existingIndex = currentHours.findIndex((h) => h.day === day);
    let newHours;
    if (existingIndex >= 0) {
      newHours = [...currentHours];
      newHours[existingIndex] = { ...newHours[existingIndex], [field]: value };
    } else {
      newHours = [...currentHours, { day: day as any, open: "09:00", close: "18:00", isOpen: true, [field]: value }];
    }
    updateBranchDetails({ operatingHours: newHours });
  };

  const applySameHoursToAll = (open: string, close: string) => {
    const newHours = daysOfWeek.map((day) => ({
      day: day.value as any,
      open,
      close,
      isOpen: true,
    }));
    updateBranchDetails({ operatingHours: newHours });
  };

  // ── Validation ─────────────────────────────────────────────────────────────

  const validateStep = (): boolean => {
    let isValid = true;

    if (!branchDetails?.branchName || branchDetails.branchName.trim().length === 0) {
      setError("branchName", "Branch name is required");
      isValid = false;
    }

    if (!branchDetails?.location || !branchDetails.location.coordinates) {
      setError("location", "Please search or click the map to set your branch location");
      isValid = false;
    } else if (!isLocationConfirmed) {
      setError("location", "Please confirm your branch location before proceeding");
      isValid = false;
    } else if (!branchDetails.location.city || branchDetails.location.city.trim().length === 0) {
      setError("location", "City is required for your branch location");
      isValid = false;
    }

    if (!isAllKerala && isLocationConfirmed && selectedHexagons.length === 0) {
      setError("hexagons", "Please select at least one hexagon for your coverage area");
      isValid = false;
    }

    return isValid;
  };

  const handleNext = () => {
    if (validateStep()) nextStep();
  };

  // Get operating hours summary
  const getHoursSummary = () => {
    const hours = branchDetails?.operatingHours;
    if (!hours || hours.length === 0) return "Not set";
    const openDays = hours.filter(h => h.isOpen).length;
    if (openDays === 0) return "Closed all days";
    if (openDays === 7) return "Open all days";
    return `${openDays} days open`;
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6 md:space-y-8">
      {/* Header */}
      <div className="border-b border-secondary-200 pb-4">
        <h2 className="text-xl md:text-2xl font-semibold text-secondary-900 mb-2">
          Primary Branch Details
        </h2>
        <p className="text-sm md:text-base text-secondary-600">
          Enter your branch information, set its location, then select your coverage hexagons
        </p>
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Left Column - Branch Form Fields */}
        <div className="space-y-5">
          {/* Branch Name */}
          <div className="bg-white rounded-lg p-4 border border-secondary-200">
            <Input
              label="Branch Name"
              placeholder="Enter branch name"
              value={branchDetails?.branchName || ""}
              onChange={(e) => handleInputChange("branchName", e.target.value)}
              error={errors.branchName as string}
              required
            />
          </div>

          {/* Branch Type Selection */}
          <div className="bg-white rounded-lg p-4 border border-secondary-200">
            <label className="block text-sm font-medium text-secondary-700 mb-3">
              Branch Type
            </label>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {branchTypes.map((type) => (
                <button
                  key={type.value}
                  type="button"
                  onClick={() => handleInputChange("branchType", type.value)}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl border-2 transition-all ${
                    branchDetails?.branchType === type.value
                      ? "border-primary-500 bg-primary-50 text-primary-700"
                      : "border-secondary-200 bg-white text-secondary-600 hover:border-primary-300 hover:bg-primary-50/50"
                  }`}
                >
                  <span className="text-2xl">{type.icon}</span>
                  <span className="text-xs font-medium">{type.label}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Contact Person */}
          <div className="bg-white rounded-lg p-4 border border-secondary-200">
            <label className="block text-sm font-medium text-secondary-700 mb-3">
              Contact Person
            </label>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Input
                label="Name"
                placeholder="Contact person name"
                value={branchDetails?.contactPerson?.name || ""}
                onChange={(e) => handleContactPersonChange("name", e.target.value)}
              />
              <Input
                label="Phone"
                placeholder="Phone number"
                value={branchDetails?.contactPerson?.phone || ""}
                onChange={(e) => handleContactPersonChange("phone", e.target.value)}
              />
            </div>
            <div className="mt-3">
              <Input
                label="Email"
                type="email"
                placeholder="email@example.com"
                value={branchDetails?.contactPerson?.email || ""}
                onChange={(e) => handleContactPersonChange("email", e.target.value)}
              />
            </div>
          </div>
        </div>

        {/* Right Column - Map & Location */}
        <div className="space-y-5">
          {/* Location Section */}
          <div className="bg-white rounded-lg p-4 border border-secondary-200">
            <label className="block text-sm font-medium text-secondary-700 mb-3">
              Branch Location <span className="text-red-500">*</span>
            </label>

            {!isLocationConfirmed ? (
              <>
                {/* Address Search */}
                <div className="relative mb-4">
                  <div className="relative">
                    <svg
                      className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-secondary-400"
                      fill="none" stroke="currentColor" viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                        d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                    </svg>
                    <input
                      type="text"
                      placeholder="Search address, landmark, or place name..."
                      value={addressSearch}
                      onChange={(e) => handleAddressInputChange(e.target.value)}
                      className="w-full pl-9 pr-10 py-2.5 border border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm"
                    />
                    {isSearching && (
                      <div className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 animate-spin rounded-full border-b-2 border-primary-500" />
                    )}
                  </div>

                  {/* Search results dropdown */}
                  {searchResults.length > 0 && (
                    <div className="absolute z-[1000] top-full left-0 right-0 mt-1 bg-white border border-secondary-200 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                      {searchResults.map((result, idx) => (
                        <button
                          key={idx}
                          type="button"
                          onClick={() => handleSelectSearchResult(result)}
                          className="w-full text-left px-4 py-2.5 hover:bg-primary-50 border-b border-secondary-100 last:border-0 text-sm text-secondary-700 transition-colors"
                        >
                          <span className="font-medium">{result.display_name.split(",")[0]}</span>
                          <span className="block text-xs text-secondary-400 truncate">{result.display_name}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                <p className="text-xs text-secondary-500 mb-3">
                  Search your branch address above, or click/drag directly on the map to pin the location.
                </p>

                {/* Map */}
                <div className="rounded-xl overflow-hidden border border-secondary-200">
                  <MapContainer
                    center={selectedLocation || DEFAULT_CENTER}
                    zoom={DEFAULT_ZOOM}
                    className="h-[380px] w-full"
                    scrollWheelZoom={true}
                  >
                    <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
                    <LocationMarker
                      onLocationSelect={handleLocationSelect}
                      selectedLocation={selectedLocation}
                    />
                    <FlyToLocation position={selectedLocation} />
                  </MapContainer>
                </div>

                {selectedLocation && (
                  <div className="mt-4 space-y-4 bg-secondary-50/50 p-4 rounded-xl border border-secondary-200">
                    <h4 className="text-sm font-semibold text-secondary-800 mb-2">
                      Verify & Edit Location Address
                    </h4>
                    
                    <div className="space-y-3">
                      <div>
                        <label className="block text-xs font-semibold text-secondary-600 mb-1">
                          Full Address
                        </label>
                        <textarea
                          value={branchDetails?.location?.address || ""}
                          onChange={(e) => handleLocationFieldChange("address", e.target.value)}
                          rows={2}
                          className="w-full px-3 py-2 border border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm bg-white"
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-secondary-600 mb-1">
                            City / Town <span className="text-red-500">*</span>
                          </label>
                          <input
                            type="text"
                            value={branchDetails?.location?.city || ""}
                            onChange={(e) => handleLocationFieldChange("city", e.target.value)}
                            className="w-full px-3 py-2 border border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm bg-white"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-secondary-600 mb-1">
                            District
                          </label>
                          <input
                            type="text"
                            value={branchDetails?.location?.district || ""}
                            onChange={(e) => handleLocationFieldChange("district", e.target.value)}
                            className="w-full px-3 py-2 border border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm bg-white"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="block text-xs font-semibold text-secondary-600 mb-1">
                            State
                          </label>
                          <input
                            type="text"
                            value={branchDetails?.location?.state || ""}
                            onChange={(e) => handleLocationFieldChange("state", e.target.value)}
                            className="w-full px-3 py-2 border border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm bg-white"
                          />
                        </div>
                        <div>
                          <label className="block text-xs font-semibold text-secondary-600 mb-1">
                            Pincode
                          </label>
                          <input
                            type="text"
                            value={branchDetails?.location?.pincode || ""}
                            onChange={(e) => handleLocationFieldChange("pincode", e.target.value)}
                            className="w-full px-3 py-2 border border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-sm bg-white"
                          />
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {selectedLocation && (
                  <div className="pt-4">
                    <button
                      type="button"
                      onClick={() => {
                        if (!branchDetails?.location?.city || branchDetails.location.city.trim().length === 0) {
                          setError("location", "City is required. Please verify the address details.");
                          return;
                        }
                        setIsLocationConfirmed(true);
                        clearError("location");
                      }}
                      className="w-full py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold text-sm transition-all shadow-md hover:shadow-lg flex items-center justify-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      Confirm Location & Choose Hexagons →
                    </button>
                  </div>
                )}
              </>
            ) : (
              /* Location Confirmed UI */
              <div className="space-y-4">
                <div className="flex items-start justify-between gap-3 p-3 bg-primary-50 rounded-lg border border-primary-200">
                  <div className="space-y-1 flex-1">
                    <span className="text-xs font-semibold text-primary-600 tracking-wider uppercase">
                      Confirmed Location
                    </span>
                    <h4 className="font-semibold text-secondary-900 text-sm">
                      {branchDetails?.location?.city || branchDetails?.location?.address?.split(",")[0] || "Pinned Location"}
                    </h4>
                    <p className="text-xs text-secondary-600 line-clamp-2">
                      {branchDetails?.location?.address || `${selectedLocation?.[0].toFixed(5)}, ${selectedLocation?.[1].toFixed(5)}`}
                    </p>
                    {branchDetails?.location && (
                      <p className="text-xs font-medium text-secondary-500 mt-1">
                        {[
                          branchDetails.location.district,
                          branchDetails.location.state,
                          branchDetails.location.pincode
                        ].filter(Boolean).join(", ")}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setIsLocationConfirmed(false);
                      setSelectedHexagons([]);
                    }}
                    className="px-3 py-1.5 border border-primary-300 hover:bg-primary-100 text-primary-700 rounded-lg text-xs font-medium transition-all shrink-0"
                  >
                    Change Location
                  </button>
                </div>

                {/* Mini static map preview */}
                <div className="rounded-lg overflow-hidden border border-secondary-200 opacity-90 h-[180px]">
                  <MapContainer
                    center={selectedLocation || DEFAULT_CENTER}
                    zoom={14}
                    className="h-full w-full pointer-events-none"
                    zoomControl={false}
                    attributionControl={false}
                    dragging={false}
                    doubleClickZoom={false}
                    scrollWheelZoom={false}
                  >
                    <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
                    {selectedLocation && <Marker position={selectedLocation} />}
                  </MapContainer>
                </div>
              </div>
            )}

            {errors.location && (
              <p className="text-sm text-red-600 mt-2">{errors.location as string}</p>
            )}
          </div>
        </div>
      </div>

      {/* Operating Hours - Full Width Enhanced */}
          <div className="bg-white rounded-lg p-5 border border-secondary-200 shadow-sm">
            <div className="flex items-center justify-between mb-4 pb-2 border-b border-secondary-100">
              <div className="flex items-center gap-2">
                <span className="text-xl">🕒</span>
                <label className="text-base font-semibold text-secondary-900">
                  Operating Hours
                </label>
              </div>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  onClick={() => applySameHoursToAll("09:00", "18:00")}
                  className="text-xs text-primary-600 hover:text-primary-700 font-semibold px-2.5 py-1.5 bg-primary-50 rounded-lg transition-all"
                >
                  Set 9 AM - 6 PM for all open days
                </button>
                <button
                  type="button"
                  onClick={() => setExpandedHours(!expandedHours)}
                  className="text-xs text-secondary-500 hover:text-secondary-700 font-medium"
                >
                  {expandedHours ? "Collapse Details" : "Expand Details"}
                </button>
              </div>
            </div>

            {/* Summary Badge */}
            <div className="mb-4 inline-block px-3 py-1 bg-secondary-100/70 rounded-full text-xs font-medium text-secondary-700">
              📅 Status Summary: {getHoursSummary()}
            </div>

            {/* Full Width Operating Hours Table */}
            {expandedHours && (
              <div className="overflow-x-auto rounded-lg border border-secondary-100">
                <table className="w-full text-sm">
                  <thead className="bg-secondary-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-secondary-600 uppercase tracking-wider">Day</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-secondary-600 uppercase tracking-wider w-32">Open Status</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-secondary-600 uppercase tracking-wider">Opening Time</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-secondary-600 uppercase tracking-wider">Closing Time</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-secondary-100 bg-white">
                    {daysOfWeek.map((day) => {
                      const hours = branchDetails?.operatingHours?.find((h) => h.day === day.value);
                      const isOpen = hours?.isOpen !== false;
                      return (
                        <tr key={day.value} className="hover:bg-secondary-50/50 transition-colors">
                          <td className="px-4 py-3.5 font-medium text-secondary-800">
                            <span className="hidden md:inline">{day.label}</span>
                            <span className="md:hidden">{day.shortLabel}</span>
                          </td>
                          <td className="px-4 py-3.5 text-center">
                            <label className="inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={isOpen}
                                onChange={(e) => handleOperatingHoursChange(day.value, "isOpen", e.target.checked)}
                                className="w-4.5 h-4.5 rounded border-secondary-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
                              />
                            </label>
                          </td>
                          <td className="px-4 py-3.5">
                            {isOpen ? (
                              <input
                                type="time"
                                value={hours?.open || "09:00"}
                                onChange={(e) => handleOperatingHoursChange(day.value, "open", e.target.value)}
                                className="w-full max-w-[150px] mx-auto px-3 py-1.5 text-sm border border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-center block bg-secondary-50/30"
                              />
                            ) : (
                              <span className="text-secondary-400 font-medium text-center block">Closed</span>
                            )}
                          </td>
                          <td className="px-4 py-3.5">
                            {isOpen ? (
                              <input
                                type="time"
                                value={hours?.close || "18:00"}
                                onChange={(e) => handleOperatingHoursChange(day.value, "close", e.target.value)}
                                className="w-full max-w-[150px] mx-auto px-3 py-1.5 text-sm border border-secondary-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent text-center block bg-secondary-50/30"
                              />
                            ) : (
                              <span className="text-secondary-400 font-medium text-center block">Closed</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <p className="text-xs text-secondary-500 mt-4 italic">
              * Configure your weekly operation times above. Uncheck the status checkbox for days your branch remains closed.
            </p>
          </div>

      {/* ── Section 2: Hexagon Selection (shown after location is set) ── */}
      {isLocationConfirmed && selectedLocation && (
        <div className="border-t border-secondary-200 pt-6 mt-4 space-y-4">
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-secondary-900">
                Select Your Coverage Hexagons
              </h3>
              <p className="text-sm text-secondary-500 mt-0.5">
                {isAllKerala
                  ? "All Kerala plan — your coverage spans the entire state."
                  : isCustom
                  ? `Custom Plan — click any hexagons on the map around your branch to select your custom coverage. ₹${costPerHex.toLocaleString()}/hex (excl. GST).`
                  : planHexLimit
                  ? `Choose up to ${planHexLimit} hexagon${planHexLimit !== 1 ? "s" : ""} from the area around your branch. Each hexagon covers ~5.16 km².`
                  : "Click hexagons on the map below to select your coverage area. Each hexagon covers ~5.16 km²."}
              </p>
            </div>

            {!isAllKerala && (
              <div className="shrink-0 text-right bg-primary-50 rounded-lg px-4 py-2 border border-primary-200">
                {planHexLimit ? (
                  <>
                    <p className={`text-lg font-bold ${
                      selectedHexagons.length >= planHexLimit ? "text-red-600" : "text-primary-600"
                    }`}>
                      {selectedHexagons.length} / {planHexLimit}
                    </p>
                    <p className="text-xs text-secondary-500">hexagons selected</p>
                    <p className="text-xs text-secondary-400 font-medium">
                      ~{(selectedHexagons.length * 5.16).toFixed(1)} km²
                    </p>
                  </>
                ) : (
                  <>
                    <p className="text-lg font-bold text-primary-600">
                      {selectedHexagons.length}
                    </p>
                    <p className="text-xs text-secondary-500">custom hexagons</p>
                    <p className="text-xs font-bold text-emerald-600">
                      ₹{(selectedHexagons.length * costPerHex).toLocaleString()}
                      <span className="text-[10px] text-secondary-400 font-normal ml-1">(excl. GST)</span>
                    </p>
                    <p className="text-xs text-secondary-400 font-medium">
                      ~{(selectedHexagons.length * 5.16).toFixed(1)} km²
                    </p>
                  </>
                )}
              </div>
            )}
          </div>

          {isAllKerala ? (
            <div className="p-4 bg-emerald-50 rounded-xl border border-emerald-200">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <p className="text-sm text-emerald-700 font-medium">
                  All Kerala Coverage — no manual hexagon selection needed. Your coverage automatically spans all of Kerala.
                </p>
              </div>
            </div>
          ) : isLoadingHexagons ? (
            <div className="flex items-center gap-3 p-5 bg-secondary-50 rounded-xl border border-secondary-200">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-primary-500" />
              <p className="text-sm text-secondary-600">Loading nearby hexagons for your branch location...</p>
            </div>
          ) : nearbyHexData.length === 0 && locationFetched ? (
            <div className="p-4 bg-amber-50 rounded-xl border border-amber-200">
              <div className="flex items-center gap-2">
                <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p className="text-sm text-amber-700">
                  No hexagons found in the 10 km radius around this location. Try selecting a different location.
                </p>
              </div>
            </div>
          ) : nearbyHexData.length > 0 ? (
            <>
              {/* Legend */}
              <div className="flex flex-wrap gap-4 text-xs text-secondary-600 p-3 bg-secondary-50 rounded-lg">
                <span className="flex items-center gap-1.5">
                  <span className="inline-block h-3 w-3 rounded-sm" style={{ background: "#4CAF50" }} />
                  Available (click to select)
                </span>
                <span className="flex items-center gap-1.5">
                  <span className="inline-block h-3 w-3 rounded-sm" style={{ background: "#2196F3" }} />
                  Selected
                </span>
                {planHexLimit && selectedHexagons.length >= planHexLimit && (
                  <span className="flex items-center gap-1.5">
                    <span className="inline-block h-3 w-3 rounded-sm" style={{ background: "#e5e7eb" }} />
                    Limit reached
                  </span>
                )}
              </div>

              {/* Hexagon selection map */}
              <div 
                ref={mapWrapperRef}
                className={isMapFullscreen ? "bg-white w-screen h-screen relative" : "rounded-xl overflow-hidden border border-secondary-200 shadow-sm relative bg-white h-[450px]"}
              >
                <button
                  type="button"
                  onClick={toggleFullscreen}
                  className="absolute top-4 right-4 z-[1000] bg-white text-secondary-700 p-2 rounded-md shadow-md hover:bg-secondary-50 border border-secondary-200 flex items-center justify-center transition-colors"
                  title="Toggle Fullscreen"
                >
                  {isMapFullscreen ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                    </svg>
                  )}
                </button>
                <MapContainer
                  center={selectedLocation}
                  zoom={13}
                  className="h-full w-full"
                  scrollWheelZoom={true}
                  preferCanvas={true}
                >
                  <MapResizer isFullscreen={isMapFullscreen} />
                  <TileLayer url={TILE_URL} attribution={TILE_ATTRIBUTION} />
                  <Marker position={selectedLocation} />
                  <HexOverlay
                    hexagons={nearbyHexData}
                    selectedHexagons={selectedHexagons}
                    onToggle={handleHexToggle}
                    maxSelections={planHexLimit}
                  />
                </MapContainer>
              </div>

              {/* Selected list */}
              {selectedHexagons.length > 0 && (
                <div className="flex items-center justify-between p-3 bg-primary-50 rounded-lg border border-primary-100">
                  <div>
                    <p className="text-sm text-primary-700">
                      <strong>{selectedHexagons.length}</strong> hexagon{selectedHexagons.length !== 1 ? "s" : ""} selected
                      {" "}(~{(selectedHexagons.length * 5.16).toFixed(1)} km² coverage)
                    </p>
                    {isCustom && (
                      <p className="text-xs text-emerald-700 font-semibold mt-0.5">
                        Plan Total: {selectedHexagons.length} × ₹{costPerHex.toLocaleString()} = ₹{(selectedHexagons.length * costPerHex).toLocaleString()} (excl. GST)
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setSelectedHexagons([])}
                    className="text-xs text-red-500 hover:text-red-700 font-medium transition-colors px-2 py-1 rounded hover:bg-red-50"
                  >
                    Clear all
                  </button>
                </div>
              )}

              {errors.hexagons && (
                <p className="text-sm text-red-600">{errors.hexagons as string}</p>
              )}

              <p className="text-xs text-secondary-500">
                Tap/click hexagons on the map above to select or deselect them.
                Only hexagons within 10 km of your branch location are shown.
              </p>
            </>
          ) : null}
        </div>
      )}

      <NavigationButtons
        onPrevious={previousStep}
        onNext={handleNext}
        nextLabel="Next: Additional Work & Income"
      />
    </div>
  );
};

export default PartnerFormStep3Branch;